import { data, useActionData, useSearchParams } from 'react-router';

import { Form } from '@veraclins-dev/form/form';
import { useConform } from '@veraclins-dev/form/hooks';
import { LabeledTextField } from '@veraclins-dev/form/labeled-text-field';
import { formSubmissionErrors, processForm } from '@veraclins-dev/form/server';
import { Box } from '@veraclins-dev/ui/box';
import { Icon } from '@veraclins-dev/ui/icon';
import { Typography } from '@veraclins-dev/ui/typography';

import { allowedDomainsLabel } from '../../utils/auth/allowed-domains';
import { sendMagicLinkEmail } from '../../utils/auth/email.server';
import { issueMagicLink } from '../../utils/auth/magic-link.server';
import { getSessionEmail, safeRedirect } from '../../utils/auth/session.server';

import { type Route } from './+types/login';
import { RequestLink } from './schema';

export const meta: Route.MetaFunction = () => [
  { title: 'Sign in — Veraclins Docs' },
];

export async function loader({ request }: Route.LoaderArgs) {
  const email = await getSessionEmail(request);
  if (email) {
    const url = new URL(request.url);
    throw new Response(null, {
      status: 302,
      headers: { location: safeRedirect(url.searchParams.get('redirectTo')) },
    });
  }
  return null;
}

export async function action({ request }: Route.ActionArgs) {
  const submission = await processForm({ request, schema: RequestLink });
  if (submission.status !== 'success') {
    return formSubmissionErrors(submission);
  }

  const url = new URL(request.url);
  const link = await issueMagicLink({
    email: submission.value.email,
    origin: url.origin,
    redirectTo: submission.redirectTo,
  });

  // link is null when the address is outside the allowed domains. Either way
  // the response is identical — the form must not reveal which case happened,
  // or it becomes a directory of who has a company mailbox.
  if (link) {
    try {
      await sendMagicLinkEmail({ to: link.email, url: link.url });
    } catch {
      // A genuine provider failure. Reachable only for an allowed address
      // (a disallowed one never gets here), so the generic copy leaks nothing
      // about domains, only that sending failed.
      return data({ status: 'send-failed' as const }, { status: 502 });
    }
  }

  return data({ status: 'sent' as const });
}

export default function Login() {
  const actionData = useActionData<typeof action>();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  const { fields, form } = useConform({
    schema: RequestLink,
    id: 'docs-login-form',
  });

  const sent = actionData?.status === 'sent';
  const sendFailed = actionData?.status === 'send-failed';

  return (
    <Box
      display="flex"
      flexDirection="column"
      items="center"
      justify="center"
      flex="1"
      minH={0}
      paddingX={4}
      paddingY={16}
    >
      <Box display="flex" flexDirection="column" gap={6} width="full" maxW="sm">
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="h1" className="font-semibold">
            Sign in to Veraclins Docs
          </Typography>
          <Typography className="text-sm text-foreground/70">
            Enter your {allowedDomainsLabel} email and we'll send you a sign-in
            link.
          </Typography>
        </Box>

        {sent ? (
          <Box
            role="status"
            border={1}
            rounded="lg"
            padding={4}
            display="flex"
            items="start"
            gap={3}
            className="border-success/30 bg-success/10"
          >
            <Icon
              name="envelope-closed"
              size="sm"
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-success"
            />
            <Typography component="span" className="text-sm">
              If that address can sign in, a link is on its way. It works once
              and expires shortly — check your inbox.
            </Typography>
          </Box>
        ) : (
          <Form
            action="/login"
            form={form}
            submitText="Send sign-in link"
            redirectTo={redirectTo}
          >
            {sendFailed ? (
              <Box
                role="alert"
                border={1}
                rounded="md"
                paddingX={4}
                paddingY={3}
                className="border-danger/30 bg-danger/10 text-sm text-danger"
              >
                We couldn't send the link right now. Please try again.
              </Box>
            ) : null}
            <LabeledTextField
              label="Work email"
              type="email"
              placeholder="you@veraclins.dev"
              autoComplete="email"
              field={fields.email}
            />
          </Form>
        )}
      </Box>
    </Box>
  );
}
